import React, { useEffect, useState } from 'react';
import { FolderIcon, FolderOpenIcon, FileIcon, XIcon, PlayIcon, CheckSquare, Square, CoinsIcon, ClockIcon, AudioLinesIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { copy } from '../copy';
type Phase = 'idle' | 'scanning' | 'done';
const scanFiles = ['Production_001/Police_Reports/report_4421.pdf', 'Production_001/Body_Cam/BWC_Miller_0412.mp4', 'Production_001/Audio_911/Dispatch_2231.mp3', 'Production_002/Surveillance/Lot_C.mp4', 'Production_002/Interviews/client_0520.txt'];
export function IngestPage() {
  const navigate = useNavigate();
  const c = copy.ingest;
  const [recursive, setRecursive] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const allEstimateIds = c.estimateGroups.flatMap(g => g.files.map(f => f.id));
  const [selected, setSelected] = useState<string[]>(allEstimateIds);
  useEffect(() => {
    if (phase !== 'scanning') return;
    let pct = 0;
    const interval = setInterval(() => {
      pct += 4;
      setProgress(Math.min(pct, 100));
      setCurrentFile(scanFiles[Math.floor(pct / 100 * scanFiles.length) % scanFiles.length]);
      if (pct >= 100) {
        clearInterval(interval);
        setPhase('done');
      }
    }, 120);
    return () => clearInterval(interval);
  }, [phase]);
  const startScan = () => {
    setProgress(0);
    setPhase('scanning');
  };
  const cancelScan = () => {
    setPhase('idle');
    setProgress(0);
  };
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allSelected = selected.length === allEstimateIds.length;
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-370">
      <Header title={c.headerTitle} minimal showBackArrow backTo="/case" data-id="element-371" />

      <main className="mx-auto max-w-4xl px-6 py-12 space-y-8" data-id="element-372">
        <div data-id="element-373">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-id="element-374">
            {c.title}
          </h1>
          <p className="text-base text-slate-500 dark:text-[#a3a3a3] mt-2 leading-relaxed" data-id="element-375">
            {c.subtitle}
          </p>
        </div>

        {/* Folder picker */}
        <section className="rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-5 hover:border-slate-300 dark:hover:border-[#444] transition-all shadow-sm" data-id="element-376">
          <div className="flex items-center justify-between" data-id="element-377">
            <div data-id="element-378">
              <h2 className="font-semibold text-base text-slate-900 dark:text-white" data-id="element-379">
                {c.pickerTitle}
              </h2>
              <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-0.5" data-id="element-380">
                {c.pickerHint}
              </p>
            </div>
            <label className="flex items-center gap-2.5 text-sm font-medium text-slate-600 dark:text-[#a3a3a3] cursor-pointer select-none hover:text-slate-900 dark:hover:text-white transition-colors" data-id="element-381">
              <input type="checkbox" checked={recursive} onChange={e => setRecursive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 dark:border-[#444] accent-blue-500 cursor-pointer" data-id="element-382" />
              {c.recursiveLabel}
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-5 shadow-sm" data-id="element-383">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#555] mb-4" data-id="element-384">
              {c.treePreviewTitle}
            </div>
            <ul className="space-y-2" data-id="element-385">
              {c.tree.map(root => <li key={root.name} data-id="element-386">
                  <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 dark:text-[#e5e5e5]" data-id="element-387">
                    <FolderOpenIcon size={16} className="text-blue-500" strokeWidth={2} data-id="element-388" />
                    {root.name}
                  </div>
                  {recursive && <ul className="ml-6 mt-2 space-y-1.5 border-l-2 border-slate-100 dark:border-[#262626] pl-4" data-id="element-389">
                      {root.children.map(child => <li key={child.name} className="flex items-center justify-between text-sm text-slate-500 dark:text-[#a3a3a3] hover:text-slate-700 dark:hover:text-white transition-colors" data-id="element-390">
                          <span className="flex items-center gap-2" data-id="element-391">
                            <FolderIcon size={14} className="text-slate-400" strokeWidth={2} data-id="element-392" />
                            {child.name}
                          </span>
                          <span className="text-xs font-medium text-slate-400 dark:text-[#555]" data-id="element-393">
                            {child.count} files
                          </span>
                        </li>)}
                    </ul>}
                </li>)}
            </ul>
          </div>

          <p className="text-xs text-slate-500 dark:text-[#555] leading-relaxed bg-slate-100 dark:bg-[#2a2a2a] rounded-xl p-3" data-id="element-394">
            {c.cheapPassNote}
          </p>

          {phase === 'idle' && <button onClick={startScan} className="flex items-center gap-2 px-5 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-[0.98] transition-all shadow-sm hover:shadow-md" data-id="element-395">
              <PlayIcon size={17} strokeWidth={2.5} data-id="element-396" />
              {c.scanButton}
            </button>}

          {phase === 'scanning' && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="space-y-4" data-id="element-397">
              <div className="space-y-2" data-id="element-398">
                <div className="flex items-center justify-between text-sm" data-id="element-399">
                  <span className="font-semibold text-slate-900 dark:text-white" data-id="element-400">
                    {c.scanningLabel}
                  </span>
                  <span className="font-bold text-blue-600 dark:text-blue-400 tabular-nums" data-id="element-401">
                    {progress}%
                  </span>
                </div>
                <div className="h-2.5 w-full bg-slate-200 dark:bg-[#333] rounded-full overflow-hidden" data-id="element-402">
                  <motion.div className="h-full bg-blue-500 rounded-full" initial={{
                width: 0
              }} animate={{
                width: `${progress}%`
              }} transition={{
                duration: 0.3
              }} data-id="element-403" />
                </div>
                <p className="text-xs text-slate-500 dark:text-[#a3a3a3] font-mono truncate" data-id="element-404">
                  {currentFile}
                </p>
              </div>
              <button onClick={cancelScan} className="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-[#333] text-slate-600 dark:text-[#a3a3a3] rounded-xl font-medium hover:bg-slate-100 dark:hover:bg-white/5 active:scale-[0.98] transition-all" data-id="element-405">
                <XIcon size={16} strokeWidth={2.5} data-id="element-406" />
                {c.cancelLabel}
              </button>
            </motion.div>}
        </section>

        {/* Estimate panel */}
        <AnimatePresence data-id="element-407">
          {phase === 'done' && <motion.section initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-5 shadow-sm" data-id="element-408">
              <div data-id="element-409">
                <h2 className="font-semibold text-base text-slate-900 dark:text-white" data-id="element-410">
                  {c.estimateTitle}
                </h2>
                <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-0.5" data-id="element-411">
                  {c.estimateSubtitle}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-412">
                <div className="rounded-xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4" data-id="element-413">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-xs font-semibold uppercase tracking-wider mb-2" data-id="element-414">
                    <AudioLinesIcon size={14} strokeWidth={2} data-id="element-415" />
                    Files
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-416">
                    {c.estimate.spokenAudioCount}
                  </div>
                </div>
                <div className="rounded-xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4" data-id="element-417">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-xs font-semibold uppercase tracking-wider mb-2" data-id="element-418">
                    <ClockIcon size={14} strokeWidth={2} data-id="element-419" />
                    Duration
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-420">
                    {c.estimate.totalDuration}
                  </div>
                </div>
                <div className="rounded-xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4" data-id="element-421">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-xs font-semibold uppercase tracking-wider mb-2" data-id="element-422">
                    Tokens
                  </div>
                  <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-423">
                    {c.estimate.tokenRangeLow}–{c.estimate.tokenRangeHigh}
                  </div>
                </div>
                <div className="rounded-xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4" data-id="element-424">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-xs font-semibold uppercase tracking-wider mb-2" data-id="element-425">
                    <CoinsIcon size={14} strokeWidth={2} data-id="element-426" />
                    Cost
                  </div>
                  <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-427">
                    {c.estimate.costLow}–{c.estimate.costHigh}
                  </div>
                </div>
              </div>

              <div className="space-y-3" data-id="element-428">
                <div className="flex items-center justify-between" data-id="element-429">
                  <label className="flex items-center gap-2.5 text-sm font-medium text-slate-600 dark:text-[#a3a3a3] cursor-pointer select-none hover:text-slate-900 dark:hover:text-white transition-colors" data-id="element-430">
                    <button onClick={() => setSelected(allSelected ? [] : allEstimateIds)} className="text-slate-400 dark:text-[#555] hover:text-blue-500 dark:hover:text-blue-400 transition-colors" data-id="element-431">
                      {allSelected ? <CheckSquare size={18} className="text-blue-500" strokeWidth={2.5} data-id="element-432" /> : <Square size={18} strokeWidth={2} data-id="element-433" />}
                    </button>
                    {c.selectAllLabel}
                  </label>
                </div>

                {c.estimateGroups.map(group => <div key={group.subfolder} className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4 space-y-2" data-id="element-434">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-[#555]" data-id="element-435">
                      {group.subfolder}
                    </div>
                    {group.files.map(file => {
                const isSelected = selected.includes(file.id);
                return <label key={file.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer transition-colors group" data-id="element-436">
                          <span className="flex items-center gap-2.5" data-id="element-437">
                            <button onClick={e => {
                      e.preventDefault();
                      toggle(file.id);
                    }} className="text-slate-400 dark:text-[#555] group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" data-id="element-438">
                              {isSelected ? <CheckSquare size={17} className="text-blue-500" strokeWidth={2.5} data-id="element-439" /> : <Square size={17} strokeWidth={2} data-id="element-440" />}
                            </button>
                            <span className="text-sm font-medium text-slate-700 dark:text-[#e5e5e5] group-hover:text-slate-900 dark:group-hover:text-white transition-colors" data-id="element-441">
                              {file.name}
                            </span>
                          </span>
                          <span className="text-xs font-semibold text-slate-400 dark:text-[#555] tabular-nums" data-id="element-442">
                            {file.duration}
                          </span>
                        </label>;
              })}
                  </div>)}
              </div>

              <button disabled={selected.length === 0} className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl font-semibold active:scale-[0.98] transition-all shadow-sm hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50" data-id="element-443">
                <PlayIcon size={17} strokeWidth={2.5} data-id="element-444" />
                {c.confirmButton} ({selected.length})
              </button>
            </motion.section>}
        </AnimatePresence>
      </main>
    </div>;
}